import * as i0 from '@angular/core';
import { input, model, signal, viewChild, effect, Component, Optional, Self, Pipe } from '@angular/core';
import { MatError } from '@angular/material/form-field';
import * as i1 from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { fromEvent } from 'rxjs';
import lpn, { PhoneNumberFormat } from 'google-libphonenumber';
import intlTelInput from 'intl-tel-input';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';
const _c0 = ["inputTel"];
const _c1 = (a0, a1) => ({
  "disabled": a0,
  "enable": a1
});
function PhoneInputComponent_Conditional_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 2)(1, "mat-error");
    i0.ɵɵtext(2);
    i0.ɵɵpipe(3, "transloco");
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(3, 1, ctx_r1.errorMessage()));
  }
}
let PhoneInputComponent = /*#__PURE__*/(() => {
  class PhoneInputComponent {
    constructor(destroyRef, ngControl) {
      this.destroyRef = destroyRef;
      this.ngControl = ngControl;
      this.maxLength = input(15);
      this.preferredCountries = input(['it']);
      this.placeholder = input();
      this.showErrorMessage = input(true);
      this.errorMessage = input('');
      this.phoneNumber = model();
      this.disabled = signal(false);
      this.name = signal(undefined);
      this.inputTel = viewChild('inputTel');
      this.phoneUtil = lpn.PhoneNumberUtil.getInstance();
      this.onChange = () => {};
      this.onTouch = () => {};
      if (this.ngControl != null) {
        this.ngControl.valueAccessor = this;
      }
      effect(() => {
        const inputTel = this.inputTel();
        if (!inputTel || !inputTel?.nativeElement || this.telInput) {
          return;
        }
        this.name.set(this.ngControl?.name);
        this.telInput = intlTelInput(inputTel.nativeElement, {
          preferredCountries: this.preferredCountries(),
          separateDialCode: true
        });
        if (this.externalPhone) {
          this.writePhoneNumber(this.externalPhone);
          this.externalPhone = undefined;
          this.setCountryInfo(this.telInput);
        } else {
          this.onCountrySelected();
        }
        fromEvent(inputTel.nativeElement, 'countrychange').pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          this.onCountrySelected();
        });
      }, {
        allowSignalWrites: true
      });
      this.destroyRef.onDestroy(() => {
        this.telInput?.destroy();
      });
    }
    writeValue(obj) {
      if (!this.telInput) {
        this.externalPhone = obj;
        return;
      }
      this.writePhoneNumber(obj);
    }
    registerOnChange(fn) {
      this.onChange = fn;
    }
    registerOnTouched(fn) {
      this.onTouch = fn;
    }
    setDisabledState(isDisabled) {
      this.disabled.set(isDisabled);
    }
    writePhoneNumber(phone) {
      if (!this.telInput) {
        return;
      }
      if (!phone) {
        this.phoneNumber.set(undefined);
        return;
      }
      this.phoneNumber.set(phone.number.split(' ').join(''));
      if (phone.dialCode) {
        this.telInput.setNumber(phone.dialCode + this.phoneNumber());
      }
    }
    onInputKeyPress(event) {
      const allowedChars = /[0-9+]/;
      const allowedCtrlChars = /[axcv]/; // Allows copy-pasting
      const allowedOtherKeys = ['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown', 'Home', 'End', 'Insert', 'Delete', 'Backspace', 'Enter'];
      if (!allowedChars.test(event.key) && !((event.ctrlKey || event.metaKey) && allowedCtrlChars.test(event.key)) && !allowedOtherKeys.includes(event.key)) {
        event.preventDefault();
      }
    }
    onChangeValue(phone) {
      if (this.oldPhoneNumber === phone) {
        return;
      }
      this.oldPhoneNumber = phone;
      const response = this.createResponse(phone);
      this.onChange(response);
    }
    onCountrySelected() {
      if (!this.telInput) {
        return;
      }
      this.setCountryInfo(this.telInput);
      const response = this.createResponse();
      this.onChange(response);
    }
    setCountryInfo(telInput) {
      const selectedCountryData = telInput.getSelectedCountryData();
      this.dialCode = selectedCountryData.dialCode;
      this.countryCode = selectedCountryData.iso2;
      if (!this.dialCode.startsWith('+')) {
        this.dialCode = `+${this.dialCode}`;
      }
    }
    createResponse(phoneNumber = this.phoneNumber() || '') {
      if (!this.countryCode || !phoneNumber || !this.dialCode) {
        return null;
      }
      let number = undefined;
      try {
        number = this.phoneUtil.parse(phoneNumber, this.countryCode);
      } catch (e) {
        // Nope
      }
      if (!number) {
        return null;
      }
      const internationalNumber = this.phoneUtil.format(number, PhoneNumberFormat.INTERNATIONAL);
      const nationalNumber = this.phoneUtil.format(number, PhoneNumberFormat.NATIONAL);
      const e164Number = this.phoneUtil.format(number, PhoneNumberFormat.E164);
      return {
        countryCode: this.countryCode,
        dialCode: this.dialCode,
        number: nationalNumber,
        internationalNumber,
        nationalNumber,
        e164Number
      };
    }
    static {
      this.ɵfac = function PhoneInputComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PhoneInputComponent)(i0.ɵɵdirectiveInject(i0.DestroyRef), i0.ɵɵdirectiveInject(i1.NgControl, 10));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: PhoneInputComponent,
        selectors: [["fut-phone-input"]],
        viewQuery: function PhoneInputComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuerySignal(ctx.inputTel, _c0, 5);
          }
          if (rf & 2) {
            i0.ɵɵqueryAdvance();
          }
        },
        inputs: {
          maxLength: [1, "maxLength"],
          preferredCountries: [1, "preferredCountries"],
          placeholder: [1, "placeholder"],
          showErrorMessage: [1, "showErrorMessage"],
          errorMessage: [1, "errorMessage"],
          phoneNumber: [1, "phoneNumber"]
        },
        outputs: {
          phoneNumber: "phoneNumberChange"
        },
        standalone: true,
        features: [i0.ɵɵStandaloneFeature],
        decls: 4,
        vars: 10,
        consts: [["inputTel", ""], ["type", "tel", 3, "blur", "keydown", "ngModelChange", "ngModel", "disabled", "maxLength", "ngClass", "placeholder", "id"], [1, "error-labels"]],
        template: function PhoneInputComponent_Template(rf, ctx) {
          if (rf & 1) {
            const _r1 = i0.ɵɵgetCurrentView();
            i0.ɵɵdeclareLet(0);
            i0.ɵɵelementStart(1, "input", 1, 0);
            i0.ɵɵlistener("blur", function PhoneInputComponent_Template_input_blur_1_listener() {
              i0.ɵɵrestoreView(_r1);
              return i0.ɵɵresetView(ctx.onTouch());
            })("keydown", function PhoneInputComponent_Template_input_keydown_1_listener($event) {
              i0.ɵɵrestoreView(_r1);
              return i0.ɵɵresetView(ctx.onInputKeyPress($event));
            })("ngModelChange", function PhoneInputComponent_Template_input_ngModelChange_1_listener($event) {
              i0.ɵɵrestoreView(_r1);
              return i0.ɵɵresetView(ctx.onChangeValue($event));
            });
            i0.ɵɵtwoWayListener("ngModelChange", function PhoneInputComponent_Template_input_ngModelChange_1_listener($event) {
              i0.ɵɵrestoreView(_r1);
              i0.ɵɵtwoWayBindingSet(ctx.phoneNumber, $event) || (ctx.phoneNumber = $event);
              return i0.ɵɵresetView($event);
            });
            i0.ɵɵelementEnd();
            i0.ɵɵtemplate(3, PhoneInputComponent_Conditional_3_Template, 4, 3, "div", 2);
          }
          if (rf & 2) {
            const disabled__r3 = ctx.disabled();
            i0.ɵɵadvance();
            i0.ɵɵpropertyInterpolate("id", ctx.name());
            i0.ɵɵtwoWayProperty("ngModel", ctx.phoneNumber);
            i0.ɵɵproperty("disabled", disabled__r3)("maxLength", ctx.maxLength())("ngClass", i0.ɵɵpureFunction2(7, _c1, disabled__r3, !disabled__r3))("placeholder", ctx.placeholder() ? ctx.placeholder() + ((ctx.ngControl == null ? null : ctx.ngControl.errors == null ? null : ctx.ngControl.errors["required"]) ? "*" : "") : ctx.placeholder());
            i0.ɵɵadvance(2);
            i0.ɵɵconditional(ctx.showErrorMessage() && !(ctx.ngControl == null ? null : ctx.ngControl.untouched) && (ctx.ngControl == null ? null : ctx.ngControl.errors == null ? null : ctx.ngControl.errors["required"]) && (ctx.ngControl == null ? null : ctx.ngControl.invalid) ? 3 : -1);
          }
        },
        dependencies: [MatError, FormsModule, i1.DefaultValueAccessor, i1.NgControlStatus, i1.NgModel, TranslocoPipe, NgClass],
        styles: ["[_nghost-%COMP%]:not(.ng-untouched).ng-invalid{--border-color: var(--fut-red-900) !important}[_nghost-%COMP%]:has(input.enable):not(.ng-invalid)     .iti:hover, [_nghost-%COMP%]:has(input.enable).ng-invalid.ng-untouched     .iti:hover{--border-color: var(--fut-primary-500)}[_nghost-%COMP%]    {--border-color: var(--mdc-outlined-text-field-outline-color, --fut-gray-200);--border-width: 1px}[_nghost-%COMP%]     .iti{width:100%;color:var(--fut-primary-500);border:var(--border-width) solid var(--border-color);border-radius:10px;padding:calc(var(--mdc-outlined-text-field-focus-outline-width) - 1px)}[_nghost-%COMP%]     .iti .iti__selected-flag{background-color:transparent}[_nghost-%COMP%]     .iti .iti__selected-flag .iti__selected-dial-code{opacity:.6}[_nghost-%COMP%]     .iti .iti__flag-container:after{content:none}[_nghost-%COMP%]     .iti .iti__selected-flag{padding:0 .5em 0 calc(1em - var(--border-width))}[_nghost-%COMP%]     .iti input{color:var(--fut-primary-500);font-size:1.2em;padding:.5rem .75rem;width:100%;background:#fff;height:2.5em;position:relative;border:none;border-radius:10px}[_nghost-%COMP%]     .iti input:focus{background-color:#fff;outline:none}[_nghost-%COMP%]     .iti:has(input:focus){--border-color: var(--fut-secondary-900);--border-width: var(--mdc-outlined-text-field-focus-outline-width);padding:0}[_nghost-%COMP%]     .iti:has(input.disabled){opacity:.4}[_nghost-%COMP%]     .iti:has(input.disabled) .iti__selected-flag{cursor:auto;background-color:transparent}.error-labels[_ngcontent-%COMP%]{font-size:var(--mat-form-field-subscript-text-size);color:var(--fut-red-900);height:20px;padding:0 16px;margin-bottom:-20px}"]
      });
    }
  }
  return PhoneInputComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function validatePhoneNumber(control) {
  const value = control.value;
  if (!value?.number) {
    return {};
  }
  const error = {
    validatePhoneNumber: {
      valid: false
    }
  };
  const phoneUtil = lpn.PhoneNumberUtil.getInstance();
  try {
    const e164Number = value.e164Number || value.dialCode + value.number;
    const phoneNumber = phoneUtil.parse(e164Number, value.countryCode);
    if (!phoneUtil.isValidNumberForRegion(phoneNumber, value.countryCode)) {
      return error;
    }
  } catch (e) {
    console.debug(e);
    return error;
  }
  return {};
}
let PhoneNumberPipe = /*#__PURE__*/(() => {
  class PhoneNumberPipe {
    transform(value) {
      if (!value) {
        return '';
      }
      return `${value.slice(0, 3)} ${value.slice(3, 6)} ${value.slice(6)}`;
    }
    static {
      this.ɵfac = function PhoneNumberPipe_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PhoneNumberPipe)();
      };
    }
    static {
      this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
        name: "phoneNumber",
        type: PhoneNumberPipe,
        pure: true,
        standalone: true
      });
    }
  }
  return PhoneNumberPipe;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { PhoneInputComponent, PhoneNumberPipe, validatePhoneNumber };
